import {
  useGetAllUniversalGridItemsQuery as useGetUniversalItemsQuery,
  useGetAllUniversalGridItemsLazyQuery as useGetUniversalItemsLazyQuery,
  GetAllUniversalGridItemsQuery,
} from '@graphql/generated';
import { useMemo } from 'react';
import { UniversalItem } from '../../../Catalog.types';
import {
  GetAllGridItemsEdge,
  GetAllGridItemsLazyQueryOptions,
  GetAllGridItemsLazyQueryResult,
  GetAllGridItemsQueryData,
  GetAllGridItemsQueryOptions,
  GetAllGridItemsQueryResult,
} from './getAllProductGridItems';

const extractUniversalItems = (fallbackData: GetAllUniversalGridItemsQuery | undefined): UniversalItem[] => {
  return fallbackData?.productUniversalSearchResults?.pagedResults?.edges.map(convertUniversalItem).filter((item): item is UniversalItem => Boolean(item)) ?? [];
};

const convertUniversalItem = (edge: NonNullable<GetAllUniversalGridItemsQuery['productUniversalSearchResults']['pagedResults']>['edges'][0]): UniversalItem | null => {
  if (edge.node.item.__typename === 'ProductUniversalCatalogItemContainer') {
    return {
      ...edge.node.item.catalogItem,
      source: edge.node.source,
    };
  } else if (edge.node.item.__typename === 'ProductExternalOfferContainer') {
    return edge.node.item.offer;
  }
  return null;
};

const convertResponseData = (data?: GetAllUniversalGridItemsQuery): GetAllGridItemsQueryData | undefined => {
  if (!data?.productUniversalSearchResults) {
    return undefined;
  }
  const pagedResults = data.productUniversalSearchResults?.pagedResults;
  return {
    productCatalogCategoryTreeRoot: data.productCatalogCategoryTreeRoot,
    productUniversalSearchResults: {
      allFilters: data.productUniversalSearchResults?.allFilters,
      pagedResults: pagedResults
        ? {
            edges: pagedResults.edges.reduce((acc, edge) => {
              const node = convertUniversalItem(edge);
              if (node) {
                acc.push({ node, cursor: edge.cursor });
              }
              return acc;
            }, new Array<GetAllGridItemsEdge>()),
            pageInfo: pagedResults.pageInfo,
          }
        : undefined,
    },
  };
};

export const useGetUniversalGridItemsQuery = (options: GetAllGridItemsQueryOptions): GetAllGridItemsQueryResult => {
  const { data, ...restQueryReturn } = useGetUniversalItemsQuery(options);

  const productList = useMemo(() => {
    return extractUniversalItems(data);
  }, [data]);

  return {
    data: convertResponseData(data),
    productList,
    ...restQueryReturn,
  } as const;
};

export const useGetUniversalGridItemsLazyQuery = (options: GetAllGridItemsLazyQueryOptions): GetAllGridItemsLazyQueryResult => {
  const [sendRequest, { data, ...restQueryReturn }] = useGetUniversalItemsLazyQuery(options);

  const fallbackProductList = useMemo(() => {
    return extractUniversalItems(data);
  }, [data]);

  return {
    sendRequest,
    fallbackProductList,
    ...restQueryReturn,
  } as const;
};
